import type { Key } from 'react'

import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { IController } from '@web/entities/controller'
import { type TDeviceNodeType, type TDeviceType } from '@web/entities/device'
import type { IGenerator } from '@web/entities/generator'
import type { IVp3701 } from '@web/entities/vp-3701'
import type {
  IVp3711MeasurementPost,
  IVp3711Module
} from '@web/entities/vp-3711'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'

export interface IDeviceState {
  deviceId: string | null
  typeDevice: TDeviceType | null
  typeTreeNode: TDeviceNodeType | null
  copyBuffer: {
    copyId: string
    type: TDeviceType
  } | null
  vp3711MeasurementPostListSse: IVp3711MeasurementPost[]
  isLoading: boolean
  vp3711ModulesSse: IVp3711Module[]
  vp3701ListSse: IVp3701[]
  generatorsSse: IGenerator[]
  controllersSse: IController[]
  expandedDevices: Key[]
}

const initialState: IDeviceState = {
  deviceId: null,
  typeDevice: null,
  typeTreeNode: null,
  copyBuffer: null,
  isLoading: false,
  vp3701ListSse: [],
  vp3711ModulesSse: [],
  vp3711MeasurementPostListSse: [],
  generatorsSse: [],
  controllersSse: [],
  expandedDevices: []
}

export const deviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {
    setDeviceId(state, action: PayloadAction<string>) {
      state.deviceId = action.payload
    },
    setTypeDevice(state, action: PayloadAction<TDeviceType>) {
      state.typeDevice = action.payload
    },
    setTypeTreeNode(state, action: PayloadAction<TDeviceNodeType>) {
      state.typeTreeNode = action.payload
    },
    setCopyBuffer(state, action: PayloadAction<IDeviceState['copyBuffer']>) {
      state.copyBuffer = action.payload
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload
    },
    setVp3701ListSse(state, action: PayloadAction<IVp3701[]>) {
      state.vp3701ListSse = action.payload
    },
    setVp3711ModulesSse(state, action: PayloadAction<IVp3711Module[]>) {
      state.vp3711ModulesSse = action.payload
    },
    setVp3711MeasurementPostListSse(
      state,
      action: PayloadAction<IVp3711MeasurementPost[]>
    ) {
      state.vp3711MeasurementPostListSse = action.payload
    },
    setGeneratorsSse(state, action: PayloadAction<IGenerator[]>) {
      state.generatorsSse = action.payload
    },
    setControllersSse(state, action: PayloadAction<IController[]>) {
      state.controllersSse = action.payload
    },
    setExpandedDevices(state, action: PayloadAction<Key[]>) {
      state.expandedDevices = action.payload
    }
  }
})

export const deviceReducer = deviceSlice.reducer
export const useDeviceActions = () => useDispatchedActions(deviceSlice.actions)
